import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Download, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPurchases = ({ user, setUser }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysFilter, setDaysFilter] = useState("30");
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [filterOrg, setFilterOrg] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [emailNotConfigured, setEmailNotConfigured] = useState(false);

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("access_token");
        const params = {};
        if (daysFilter !== "all") {
          params.days = parseInt(daysFilter, 10);
        }
        const resp = await axios.get(`${API}/admin/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        setPurchases(resp.data || []);
      } catch (err) {
        console.error("Purchases load error:", err);
        const msg = err.response?.data?.detail || "Error loading purchases report";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [daysFilter]);

  // Filter purchases based on selected org and category
  const filteredPurchases = purchases.filter((p) => {
    if (filterOrg !== "all" && (p.organization || "Public") !== filterOrg) {
      return false;
    }
    if (filterCategory !== "all" && (p.category || "Document") !== filterCategory) {
      return false;
    }
    return true;
  });

  // Calculate totals
  const totalRevenue = filteredPurchases.reduce((sum, p) => sum + (p.amount || 0), 0);

  // Get unique organizations and categories
  const uniqueOrgs = Array.from(new Set(purchases.map((p) => p.organization || "Public")))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  const uniqueCategories = Array.from(new Set(purchases.map((p) => p.category || "Document")))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  // Calculate totals by category
  const categoryTotals = {};
  filteredPurchases.forEach((p) => {
    const cat = p.category || "Document";
    if (!categoryTotals[cat]) {
      categoryTotals[cat] = { count: 0, amount: 0 };
    }
    categoryTotals[cat].count += 1;
    categoryTotals[cat].amount += p.amount || 0;
  });

  // Calculate totals by organization
  const orgTotals = {};
  filteredPurchases.forEach((p) => {
    const org = p.organization || "Public";
    if (!orgTotals[org]) {
      orgTotals[org] = { count: 0, amount: 0 };
    }
    orgTotals[org].count += 1;
    orgTotals[org].amount += p.amount || 0;
  });

  // CSV export function
  const downloadCsv = () => {
    const rows = [
      ["Date", "Item Title", "Category", "Item ID", "Buyer", "Amount (USD)", "Status", "Organization"],
      ...filteredPurchases.map((p) => {
        const created = p.created_at ? new Date(p.created_at) : null;
        const dateStr = created ? created.toISOString() : "";
        return [
          dateStr,
          p.item_title,
          p.category || "Document",
          p.item_id,
          p.user_email,
          (p.amount || 0).toFixed(2),
          p.purchase_status,
          p.organization || "Public",
        ];
      }),
    ];

    const csvContent = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `purchases_report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resendInvoice = async (purchaseId, userEmail) => {
    if (!window.confirm(`Resend invoice to ${userEmail}?`)) return;
    
    setSendingInvoice(purchaseId);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(`${API}/admin/purchases/${purchaseId}/resend-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.success) {
        toast.success(`Invoice sent to ${userEmail}`);
      } else {
        const msg = resp.data.message || "Failed to send invoice";
        if (msg.toLowerCase().includes("not configured") || msg.toLowerCase().includes("resend_apikey")) {
          setEmailNotConfigured(true);
          toast.error(
            "Invoice email service is not configured on the server (missing RESEND_API_KEY). Please contact support.",
          );
        } else {
          toast.error(msg);
        }
      }
    } catch (err) {
      console.error("Resend invoice error:", err);
      const detail = err.response?.data?.detail;
      if (detail && detail.toLowerCase().includes("not configured")) {
        setEmailNotConfigured(true);
        toast.error(
          "Invoice email service is not configured on the server (missing RESEND_API_KEY). Please contact support.",
        );
      } else {
        toast.error(detail || "Failed to resend invoice");
      }
    } finally {
      setSendingInvoice(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1
              className="text-xl font-semibold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="purchases" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {emailNotConfigured && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            Invoice emails cannot be sent right now because the email service is not configured on the server
            (missing <code>RESEND_API_KEY</code>). Please update the backend configuration or contact support.
          </div>
        )}
        {/* Header with filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "Fraunces, serif" }}>
              Purchases Report
            </h2>
            <p className="text-xs text-slate-500">Completed purchases for paid items</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
            {/* Date Range Filter */}
            <div className="flex items-center gap-1">
              <span>Date:</span>
              <select
                value={daysFilter}
                onChange={(e) => setDaysFilter(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 bg-white"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="all">All time</option>
              </select>
            </div>
            {/* Category Filter */}
            <div className="flex items-center gap-1">
              <span>Category:</span>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 bg-white"
                data-testid="filter-category"
              >
                <option value="all">All</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            {/* Organization Filter */}
            <div className="flex items-center gap-1">
              <span>Organization:</span>
              <select
                value={filterOrg}
                onChange={(e) => setFilterOrg(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 bg-white"
                data-testid="filter-organization"
              >
                <option value="all">All</option>
                {uniqueOrgs.map((org) => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            </div>
            {/* CSV Export Button */}
            <Button
              onClick={downloadCsv}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              disabled={filteredPurchases.length === 0}
            >
              <Download className="w-3 h-3" />
              Export CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-sm text-slate-600">
            Loading purchases...
            <BlueCubeSpinner />
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : purchases.length === 0 ? (
          <div className="text-sm text-slate-600">No completed purchases found.</div>
        ) : (
          <>
            {/* Overall Total Banner */}
            <div className="mb-4 bg-orange-50 border border-orange-200 rounded-md p-4 flex items-center justify-between">
              <div className="text-sm text-orange-800 font-medium">
                Overall Total {(filterOrg !== "all" || filterCategory !== "all") ? "(filtered)" : ""}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-orange-900 font-mono">
                  ${totalRevenue.toFixed(2)} USD
                </div>
                <div className="text-xs text-orange-700">
                  {filteredPurchases.length} purchase{filteredPurchases.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {/* Totals Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Total by Category */}
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <div className="text-xs font-semibold text-slate-600">Total by Category</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {Object.entries(categoryTotals)
                    .sort((a, b) => b[1].amount - a[1].amount)
                    .map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between px-4 py-2 text-sm">
                        <div>
                          <span className="text-slate-800">{cat}</span>
                          <span className="text-xs text-slate-400 ml-2">({data.count})</span>
                        </div>
                        <div className="font-mono font-medium text-slate-900">${data.amount.toFixed(2)}</div>
                      </div>
                    ))}
                  {Object.keys(categoryTotals).length === 0 && (
                    <div className="px-4 py-2 text-sm text-slate-500">No data</div>
                  )}
                </div>
              </div>

              {/* Total by Organization */}
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <div className="text-xs font-semibold text-slate-600">Total by Organization</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {Object.entries(orgTotals)
                    .sort((a, b) => b[1].amount - a[1].amount)
                    .map(([org, data]) => (
                      <div key={org} className="flex items-center justify-between px-4 py-2 text-sm">
                        <div>
                          <span className="text-slate-800">{org}</span>
                          <span className="text-xs text-slate-400 ml-2">({data.count})</span>
                        </div>
                        <div className="font-mono font-medium text-slate-900">${data.amount.toFixed(2)}</div>
                      </div>
                    ))}
                  {Object.keys(orgTotals).length === 0 && (
                    <div className="px-4 py-2 text-sm text-slate-500">No data</div>
                  )}
                </div>
              </div>
            </div>

            {/* Detailed Table */}
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Buyer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Organization</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredPurchases.map((p) => {
                    const created = p.created_at ? new Date(p.created_at) : null;
                    const dateStr = created
                      ? created.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                      : "-";

                    return (
                      <tr key={p.id}>
                        <td className="px-4 py-3 text-xs text-slate-600">{dateStr}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 truncate max-w-xs" title={p.item_title}>
                            {p.item_title}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{p.category || "Document"}</td>
                        <td className="px-4 py-3 text-xs text-slate-700">{p.user_email}</td>
                        <td className="px-4 py-3 text-xs text-slate-700">{p.organization || "Public"}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-900">${(p.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendInvoice(p.id, p.user_email)}
                            disabled={sendingInvoice === p.id}
                            className="text-blue-600 hover:text-blue-700 h-7 px-2"
                            title="Resend invoice"
                          >
                            {sendingInvoice === p.id ? (
                              <span className="text-xs">Sending...</span>
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredPurchases.length === 0 && purchases.length > 0 && (
              <div className="mt-4 text-sm text-slate-500 text-center">
                No purchases match the selected filters.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPurchases;
